import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Undo2 } from 'lucide-react';
import { invalidateAssetDataForNodeChange } from '@/hooks/websocket/mergeAssetPayload';
import { FavoriteNodeCommand } from '@/hooks/commands/favorite/FavoriteNodeCommand';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { favoriteNode, unfavoriteNode } from '@/services/tree.service';
import { useCommandContext } from '@/context/CommandContext';
import { useToast } from '@/components/ui/use-toast';
import { DAMNodeType, NodeType } from '@/types/tree';
import { ToastAction } from '@/components/ui/toast';
import { QueryKey } from '@/lib/queryKeys';
import { isAsset } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export type FavoriteVariables = { nodeId: string; next: boolean; nodeType?: NodeType | DAMNodeType; silent?: boolean };

type Ctx = {
  prevAssetData: Array<[unknown, any]>;
  prevTrees?: Array<[unknown, any]>;
  prevTreeSearch?: Array<[unknown, any]>;
};

export function useNodeFavorite() {
  const queryClient = useQueryClient();

  const { toast } = useToast();
  const { apply, undo } = useCommandContext();

  const tCommon = useTranslations('common');
  const tToasts = useTranslations('toasts');
  const doFavorite = useAuthenticatedQueryFn(favoriteNode);
  const doUnfavorite = useAuthenticatedQueryFn(unfavoriteNode);

  const mutation = useMutation<unknown, unknown, FavoriteVariables, Ctx>({
    mutationFn: ({ nodeId, next }: FavoriteVariables) =>
      next ? doFavorite({ node_id: nodeId }) : doUnfavorite({ node_id: nodeId }),
    onMutate: async ({ nodeId, next, nodeType, silent }: FavoriteVariables) => {
      // Stop in-flight assetData queries to avoid races
      await queryClient.cancelQueries({ queryKey: [QueryKey.AssetData] });

      // Snapshot all assetData caches to allow rollback
      const prevAssetData = queryClient.getQueriesData<any>({ queryKey: [QueryKey.AssetData] });

      const isObj = (v: any): v is Record<string, any> => Boolean(v) && typeof v === 'object';

      const patchOne = (key: ReadonlyArray<unknown>, data: any) => {
        if (!isObj(data)) {
          return data;
        }

        // read params from the queryKey: [QueryKey.AssetData, { albumId?/folderId?, ... }]
        const params = isObj(key?.[1]) ? (key[1] as any) : undefined;

        const matchesView =
          (params?.albumId && params.albumId === nodeId) ||
          (params?.folderId && params.folderId === nodeId) ||
          data.nodeId === nodeId;

        let changed = false;
        const nextPayload = { ...data };

        // 1) Folder/Album: flip the container favorite shown in the UI
        if ((nodeType === DAMNodeType.Folder || nodeType === DAMNodeType.Album) && matchesView) {
          if (nextPayload.favorite !== next) {
            nextPayload.favorite = next;
            changed = true;
          }
        }

        // 2) Asset: flip any matching row(s) on the page
        if (isAsset(nodeType) && isObj(nextPayload.assets) && Array.isArray(nextPayload.assets.assets)) {
          let rowChanged = false;
          const rows = nextPayload.assets.assets.map((r: any) => {
            if (isObj(r) && r.id === nodeId && r.favorite !== next) {
              rowChanged = true;
              return { ...r, favorite: next };
            }
            return r;
          });
          if (rowChanged) {
            nextPayload.assets = { ...nextPayload.assets, assets: rows };
            changed = true;
          }
        }

        if (!changed) {
          return data;
        }
        return nextPayload;
      };

      // Apply patch to every cached ['assetData', ...] entry
      for (const [key, value] of prevAssetData) {
        const patched = patchOne(key, value);
        if (patched !== value) {
          queryClient.setQueryData(key, patched);
        }
      }

      // Patch ALL tree caches (objects and arrays)
      const treeEntries = queryClient.getQueriesData<any>({ queryKey: [QueryKey.Tree] });
      const prevTrees: Array<[unknown, any]> = [];

      for (const [key, data] of treeEntries) {
        let changed = false;

        if (Array.isArray(data)) {
          // Root-level list: update the matching item by id
          const patched = data.map((item: any) =>
            item?.id === nodeId && item.favorite !== next ? { ...item, favorite: next } : item,
          );
          // shallow compare by reference to avoid unnecessary writes
          if (patched.some((it, i) => it !== data[i])) {
            prevTrees.push([key, data]);
            queryClient.setQueryData(key, patched);
          }
        } else if (data && typeof data === 'object') {
          let patched = data;

          // If this cache is exactly the node, flip it
          if (patched.id === nodeId && patched.favorite !== next) {
            patched = { ...patched, favorite: next };
            changed = true;
          }

          // Or if it has children, flip the matching child
          if (Array.isArray(patched.children)) {
            const children = patched.children.map((c: any) =>
              c?.id === nodeId && c.favorite !== next ? { ...c, favorite: next } : c,
            );
            if (children.some((c: any, i: number) => c !== patched.children[i])) {
              patched = { ...patched, children };
              changed = true;
            }
          }

          if (changed) {
            prevTrees.push([key, data]);
            queryClient.setQueryData(key, patched);
          }
        }
      }

      const treeSearchEntries = queryClient.getQueriesData<any>({ queryKey: [QueryKey.TreeSearch] });
      const prevTreeSearch: Array<[unknown, any]> = [];

      for (const [key, data] of treeSearchEntries) {
        if (!Array.isArray(data)) {
          continue;
        }

        const patched = data.map((item: any) =>
          item?.id === nodeId && item.favorite !== next ? { ...item, favorite: next } : item,
        );

        if (patched.some((item, index) => item !== data[index])) {
          prevTreeSearch.push([key, data]);
          queryClient.setQueryData(key, patched);
        }
      }

      if (!silent) {
        toast({
          title: next ? tToasts('favorite.added.title') : tToasts('favorite.removed.title'),
          description: `${
            nodeType === NodeType.Albums || nodeType === DAMNodeType.Album
              ? 'Album'
              : nodeType === NodeType.Folders || nodeType === DAMNodeType.Folder
                ? 'Folder'
                : 'Asset'
          } has been ${next ? 'added to' : 'removed from'} favorites`,
          action: nodeType ? (
            <ToastAction altText={tCommon('undo')} onClick={undo} icon={Undo2}>
              {tCommon('undo')}
            </ToastAction>
          ) : undefined,
        });
      }

      return { prevAssetData, prevTrees, prevTreeSearch };
    },
    onError: (_err, _vars, ctx) => {
      // rollback assetData
      ctx?.prevAssetData?.forEach(([k, v]) => queryClient.setQueryData(k as any, v));
      // rollback any tree entries we patched (root arrays or object nodes)
      ctx?.prevTrees?.forEach(([k, v]) => queryClient.setQueryData(k as any, v));
      // rollback any tree search entries
      ctx?.prevTreeSearch?.forEach(([k, v]) => queryClient.setQueryData(k as any, v));
      toast({ title: tToasts('favorite.updateFailed.title'), description: 'Please try again.' });
    },
    onSettled: async (_data, _err, vars) => {
      const { nodeId, nodeType } = vars;

      if (
        nodeType === NodeType.Folders ||
        nodeType === NodeType.Albums ||
        nodeType === DAMNodeType.Folder ||
        nodeType === DAMNodeType.Album
      ) {
        await invalidateAssetDataForNodeChange(queryClient);
      } else if (nodeType === NodeType.Assets || isAsset(nodeType)) {
        await queryClient.invalidateQueries({ queryKey: [QueryKey.Asset, nodeId] });
        await invalidateAssetDataForNodeChange(queryClient);
      }

      await queryClient.invalidateQueries({ queryKey: [QueryKey.Home] });
      await queryClient.refetchQueries({ queryKey: [QueryKey.Home], type: 'all' });
    },
  });

  const setFavorite = (nodeId: string, current: boolean, next: boolean, nodeType: NodeType | DAMNodeType) => {
    const command = FavoriteNodeCommand(nodeId, current, next, nodeType, (vars) => mutation.mutateAsync(vars));
    apply(command);
  };
  const toggleFavorite = (nodeId: string, current: boolean, nodeType: NodeType | DAMNodeType) =>
    setFavorite(nodeId, current, !current, nodeType);

  return {
    setFavorite,
    toggleFavorite,
    isFavoritePending: mutation.isPending,
  };
}
